import { type DiscoveredBridge, HttpBridge, isSpeakable } from '@kinoq/camera-protocol';
import { useEffect, useMemo, useState } from 'react';
import { bridgeUrl, desktopApi, discoveryFor } from './desktopDiscovery';

export interface BridgeCamera {
  bridgeId: string;
  cameraId: string;
  /** `<bridge name> · <camera label>` */
  label: string;
}

// A crashed bridge stays advertised until mDNS gives up on it, which can take over an hour,
// so a dead one must not hold the list up for long.
const LIST_TIMEOUT_MS = 3000;

export async function listBridgeCameras(
  bridge: Pick<DiscoveredBridge, 'id' | 'name'>,
): Promise<BridgeCamera[]> {
  const cameras = await new HttpBridge(bridgeUrl(bridge.id), LIST_TIMEOUT_MS).listCameras();
  return cameras
    .filter((c) => !c.unusable)
    .map((c) => ({ bridgeId: bridge.id, cameraId: c.id, label: `${bridge.name} · ${c.label}` }));
}

/**
 * Cameras on the bridges the desktop shell has discovered, listed one bridge at a time so
 * a dead bridge only drops out. Empty outside the desktop shell.
 */
export function useBridgeCameras(): BridgeCamera[] {
  const api = desktopApi();
  const [byBridge, setByBridge] = useState<ReadonlyMap<string, BridgeCamera[]>>(new Map());

  useEffect(() => {
    if (!api) return;
    const discovery = discoveryFor(api);
    // One token per listing in flight, so a bridge lost and found again keeps only its
    // latest answer.
    const listing = new Map<string, object>();
    const sync = () => {
      const bridges = discovery.getState().bridges.filter(isSpeakable);
      const live = new Set(bridges.map((b) => b.id));
      for (const id of listing.keys()) if (!live.has(id)) listing.delete(id);
      setByBridge((prev) => {
        if ([...prev.keys()].every((id) => live.has(id))) return prev;
        return new Map([...prev].filter(([id]) => live.has(id)));
      });
      for (const bridge of bridges) {
        if (listing.has(bridge.id)) continue;
        const token = {};
        listing.set(bridge.id, token);
        listBridgeCameras(bridge).then(
          (cameras) => {
            if (listing.get(bridge.id) === token) {
              setByBridge((prev) => new Map(prev).set(bridge.id, cameras));
            }
          },
          () => {},
        );
      }
    };
    const unsubscribe = discovery.subscribe(sync);
    discovery.start();
    sync();
    return () => {
      listing.clear();
      unsubscribe();
      discovery.stop();
    };
  }, [api]);

  return useMemo(
    () => [...byBridge.values()].flat().sort((a, b) => a.label.localeCompare(b.label)),
    [byBridge],
  );
}
